import { WarningAmber as WarningAmberIcon } from '@mui/icons-material';
import {
  Box,
  LinearProgress,
  Paper,
  Typography,
  useTheme
} from '@mui/material';
import { FC } from 'react';

import { useTranslations } from 'context/TranslationContext';

import { CONFIDENCE_STATUS_COLORS } from 'utils/constants/confidenceStatusColors';
import { OverallConfidence } from 'utils/helpers/confidenceHelpers';

import { cardContentStyle, cardHeaderStyle } from 'styles/shared/CardStyle';

interface OverallConfidenceBannerProps {
  result: OverallConfidence;
}

const SUMMARY_TEXT_COLOR = '#9ca3af';

export const OverallConfidenceBanner: FC<OverallConfidenceBannerProps> = ({
  result
}) => {
  const theme = useTheme();
  const { translate } = useTranslations();

  const isBlocking = result.state === 'blocking';
  const isMissingData = result.reason === 'missingData';
  const isValidationError = result.reason === 'validationError';
  // missing data and validation errors have no meaningful score, so they get the
  // red "fix this" treatment instead of the neutral numeric treatment
  const isBlockedWithoutScore = isMissingData || isValidationError;
  const isNeedsReview = result.state === 'needsReview';

  const accentColor = isBlockedWithoutScore
    ? CONFIDENCE_STATUS_COLORS.missingData.main
    : isBlocking
      ? CONFIDENCE_STATUS_COLORS.lowConfidence.main
      : isNeedsReview
        ? CONFIDENCE_STATUS_COLORS.needsReview.main
        : CONFIDENCE_STATUS_COLORS.confident.main;

  const borderColor = isBlockedWithoutScore
    ? CONFIDENCE_STATUS_COLORS.missingData.main
    : '#e5e7eb';

  const percentage =
    result.score === null ? '–' : `${Math.round(result.score * 100)}%`;

  const needsReviewMessage =
    result.sectionsNeedingReview === 1
      ? translate('overallConfidence.oneSectionNeedsReview')
      : translate('overallConfidence.sectionsNeedReview', {
          count: String(result.sectionsNeedingReview)
        });

  const message = (() => {
    if (result.state === 'confident') {
      return translate('overallConfidence.confidentMessage');
    }
    if (isMissingData) {
      return translate('overallConfidence.missingDataMessage');
    }
    if (isValidationError) {
      return translate('overallConfidence.validationErrorMessage');
    }
    if (isBlocking) {
      return translate('overallConfidence.blockingMessage');
    }
    return needsReviewMessage;
  })();

  return (
    <Paper
      elevation={0}
      data-testid="overall-confidence-banner"
      sx={{
        borderRadius: '1rem',
        border: `1px solid ${borderColor}`,
        borderLeft: `2px solid ${borderColor}`
      }}
    >
      <Box
        data-testid="overall-confidence-header"
        sx={{
          ...cardHeaderStyle,
          justifyContent: 'space-between',
          ...(isBlockedWithoutScore
            ? {
                backgroundColor: CONFIDENCE_STATUS_COLORS.missingData.background
              }
            : {})
        }}
      >
        <Typography
          data-testid="overall-confidence-title"
          variant="body1"
          sx={{
            fontSize: '0.75rem',
            fontWeight: 600,
            flexShrink: 0,
            color: isBlockedWithoutScore
              ? CONFIDENCE_STATUS_COLORS.missingData.text
              : 'inherit'
          }}
        >
          {translate('overallConfidence.title')}
        </Typography>

        <Typography
          data-testid="overall-confidence-percentage"
          variant="body1"
          sx={{
            fontSize: '0.75rem',
            fontWeight: 600,
            color: accentColor
          }}
        >
          {percentage}
        </Typography>
      </Box>

      <Box
        data-testid="overall-confidence-content"
        sx={{
          ...cardContentStyle,
          ...(isBlockedWithoutScore ? { minHeight: '50px' } : {})
        }}
      >
        {result.score !== null && (
          <LinearProgress
            data-testid="overall-confidence-progress"
            variant="determinate"
            value={Math.round(result.score * 100)}
            sx={{
              height: 6,
              borderRadius: 3,
              backgroundColor: '#e5e7eb',
              '& .MuiLinearProgress-bar': {
                backgroundColor: accentColor
              }
            }}
          />
        )}

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'nowrap',
            gap: 0.5
          }}
        >
          {isBlockedWithoutScore && (
            <WarningAmberIcon
              data-testid="overall-confidence-warning-icon"
              sx={{
                color: CONFIDENCE_STATUS_COLORS.missingData.main,
                fontSize: 16,
                flexShrink: 0
              }}
            />
          )}
          <Typography
            data-testid="overall-confidence-message"
            variant="body2"
            sx={{
              fontSize: isBlockedWithoutScore ? '14px' : '0.75rem',
              textAlign: 'left',
              color: isNeedsReview
                ? SUMMARY_TEXT_COLOR
                : isBlockedWithoutScore
                  ? CONFIDENCE_STATUS_COLORS.missingData.text
                  : isBlocking
                    ? CONFIDENCE_STATUS_COLORS.lowConfidence.text
                    : theme.palette.text.primary
            }}
          >
            {message}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};
